import { Box, Chip, Skeleton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { DateTime } from "luxon";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE, INK, NEUTRAL } from "../../theme/tokens";
import { formatPtoTimeRange } from "../../utils/dateUtils";
import type { AffectedShift } from "../../utils/models";

const LIST_MAX_HEIGHT = 160;
const SKELETON_ROW_KEYS = ["first", "second"];
const SKELETON_ROW_HEIGHT = 28;
// A stale answer reads as stale without a spinner taking the list's place.
const STALE_OPACITY = 0.45;

type ChipPalette = "error" | "warning" | "info";

const OVERLAP_DISPLAY: Record<AffectedShift["overlap"], { labelKey: string; color: ChipPalette }> = {
	remove: { labelKey: "affected.shift.removed", color: "error" },
	trim_start: { labelKey: "affected.shift.trimmed", color: "warning" },
	trim_end: { labelKey: "affected.shift.trimmed", color: "warning" },
	split: { labelKey: "affected.shift.split", color: "info" },
};

/** A removed shift the window only partly covers — removed because the leftover would be under half the shift. */
const isRemovedWithoutFullCover = (shift: AffectedShift, windowStart: DateTime, windowEnd: DateTime): boolean =>
	shift.overlap === "remove" && (windowStart > shift.startTime || windowEnd < shift.endTime);

/** The working time left on the shift after the PTO window is carved out (none for removed shifts). */
const remainingRanges = (shift: AffectedShift, windowStart: DateTime, windowEnd: DateTime): string | null => {
	switch (shift.overlap) {
		case "trim_start":
			return formatPtoTimeRange(windowEnd, shift.endTime);
		case "trim_end":
			return formatPtoTimeRange(shift.startTime, windowStart);
		case "split":
			return `${formatPtoTimeRange(shift.startTime, windowStart)} & ${formatPtoTimeRange(windowEnd, shift.endTime)}`;
		default:
			return null;
	}
};

type AffectedShiftsSectionProps = {
	shifts: AffectedShift[];
	windowStart: DateTime;
	windowEnd: DateTime;
	isEdit: boolean;
	/** First fetch for this dialog — nothing has ever been shown, so placeholder rows stand in. */
	isLoading: boolean;
	/** A newer window is in flight while a previous answer is on screen: dim it, never blank it. */
	isRefreshing: boolean;
	isError: boolean;
};

export const AffectedShiftsSection: FC<AffectedShiftsSectionProps> = ({ shifts, windowStart, windowEnd, isEdit, isLoading, isRefreshing, isError }) => {
	const { t } = useTranslation();

	if (isLoading) {
		return (
			<Box data-testid="add-day-off-affected-shifts">
				<Typography sx={{ color: alpha(INK.SECONDARY, 0.5), fontSize: "13px", fontWeight: 600 }}>{t("affected.shifts.title")}</Typography>
				<Box
					sx={{ border: `1px solid ${NEUTRAL[300]}`, borderRadius: 1 }}
					data-testid="add-day-off-affected-shifts-skeleton"
					role="status"
					aria-busy
					aria-label={t("affected.shifts.title")}
				>
					{SKELETON_ROW_KEYS.map((rowKey) => (
						<Box key={rowKey} sx={{ px: 1, py: 0.5, "&:not(:last-child)": { borderBottom: `1px solid ${NEUTRAL[200]}` } }}>
							<Skeleton variant="rounded" height={SKELETON_ROW_HEIGHT} />
						</Box>
					))}
				</Box>
			</Box>
		);
	}

	if (isError) {
		return (
			<Box data-testid="add-day-off-affected-shifts">
				<Typography sx={{ color: alpha(INK.SECONDARY, 0.5), fontSize: "13px", fontWeight: 600 }}>{t("affected.shifts.title")}</Typography>
				<Typography sx={{ color: alpha(INK.SECONDARY, 0.7), fontSize: "13px" }} data-testid="add-day-off-affected-shifts-error" role="alert">
					{t("affected.shifts.error")}
				</Typography>
			</Box>
		);
	}

	return (
		<Box
			data-testid="add-day-off-affected-shifts"
			aria-busy={isRefreshing}
			sx={(theme) => ({
				opacity: isRefreshing ? STALE_OPACITY : 1,
				transition: theme.transitions.create("opacity", { duration: theme.transitions.duration.shorter }),
			})}
		>
			<Typography sx={{ color: alpha(INK.SECONDARY, 0.5), fontSize: "13px", fontWeight: 600 }}>{t("affected.shifts", { count: shifts.length })}</Typography>
			{shifts.length === 0 ? (
				<Typography sx={{ color: alpha(INK.SECONDARY, 0.7), fontSize: "13px" }} data-testid="add-day-off-affected-shifts-none">
					{t("affected.shifts.none")}
				</Typography>
			) : (
				<Box sx={{ maxHeight: LIST_MAX_HEIGHT, overflowY: "auto", border: `1px solid ${NEUTRAL[300]}`, borderRadius: 1 }}>
					{shifts.map((shift) => {
						const { labelKey, color } = OVERLAP_DISPLAY[shift.overlap];
						const remaining = remainingRanges(shift, windowStart, windowEnd);
						return (
							<Box
								key={shift.id}
								data-testid={`add-day-off-affected-shift-row-${shift.id}`}
								sx={{ px: 1, py: 0.5, "&:not(:last-child)": { borderBottom: `1px solid ${NEUTRAL[200]}` } }}
							>
								<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
									<Typography sx={{ color: INK.SECONDARY, fontSize: "13px" }}>
										{`${shift.startTime.toFormat("ccc, MMM d")} · ${formatPtoTimeRange(shift.startTime, shift.endTime)}`}
									</Typography>
									<Chip
										label={t(labelKey)}
										size="small"
										data-testid={`add-day-off-affected-shift-status-${shift.overlap}`}
										sx={(theme) => ({
											backgroundColor: alpha(theme.palette[color].main, 0.12),
											color: theme.palette[color].dark,
											fontWeight: 600,
										})}
									/>
								</Box>
								{remaining && (
									<Typography sx={{ color: alpha(INK.SECONDARY, 0.7), fontSize: "12px" }}>{t("affected.shift.stays", { times: remaining })}</Typography>
								)}
								{isRemovedWithoutFullCover(shift, windowStart, windowEnd) && (
									<Typography
										sx={{ color: alpha(INK.SECONDARY, 0.7), fontSize: FONT_SIZE.CAPTION }}
										data-testid={`add-day-off-affected-shift-mostly-covered-${shift.id}`}
									>
										{t("affected.shift.mostly.covered")}
									</Typography>
								)}
							</Box>
						);
					})}
				</Box>
			)}
			{isEdit && (
				<Typography sx={{ color: alpha(INK.SECONDARY, 0.7), fontSize: "12px", mt: 0.5 }} data-testid="add-day-off-affected-shifts-edit-note">
					{t("affected.shifts.edit.note")}
				</Typography>
			)}
		</Box>
	);
};
